import type {
  AprBlock, AprSlot, Block, Cell, ChipCell, Column, KvItem, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import type { Caller } from './handles'
import { callerBeside, closureApprovalRefusal } from './writes'
import {
  acknowledgeForm, approveClosureForm, escalateForm, noteForm, openCaseForm,
  proposeClosureForm, resolveForm, withdrawClosureForm, type CaseStanding,
} from './amlcontrols'

/**
 * Risk › AML cases, read from `GET /v1/admin/aml/cases` (go-wallet#82).
 *
 * The rail calls this screen "AML escalations" and the screen calls itself
 * what it is. See the `title` on the composed screen for why the two differ
 * and why `NAV` is not the thing that changes.
 *
 * # The state of a case is the whole screen
 *
 * The backend runs a four-state machine — `open`, `escalated`,
 * `closure_proposed`, `closed` — and the only dangerous direction in it is
 * closing a case with no action. So a closure is PROPOSED by one operator and
 * APPROVED by a different one, and the proposal has no effect whatsoever:
 * nothing stops being watched, and the case stays in this queue.
 *
 * `closure_proposed` is therefore the state this adapter is most careful about.
 * Rendered as anything resembling "resolved", it becomes the burial the
 * maker-checker rule exists to prevent, reached by a different route — one
 * operator proposes, the queue reads as handled, and nobody countersigns. Every
 * place that state is drawn here says, in words, that it is not a closed case.
 *
 * # The corpus draws three things this endpoint does not send
 *
 * `rk-aml` in the design corpus has a Value column, an SLA bar and a customer
 * name. The queue read has no amount, no SLA target and no name — only a
 * customer id. None of the three is improvised:
 *
 *   - The Value column is DROPPED rather than zero-filled. An `a` cell is
 *     structurally `[minor, currency]`, and `[0, 'EUR']` on an AML case is a
 *     fabricated figure on a compliance screen. There is no money on this
 *     screen at all, which is why nothing here imports `formatMinor` — there is
 *     nothing to format, and a formatter in the file would be an invitation.
 *   - The SLA bar is DROPPED rather than computed. A percent needs a target,
 *     the endpoint sends none, and a bar drawn against an invented 48h clock
 *     would be this console inventing compliance policy.
 *   - The customer column is relabelled to what it actually holds: an id.
 *
 * All three are named in the screen's own provenance, so the badge says
 * `Partial` and the operator is told which parts of the designed screen this
 * read cannot answer for. That keeps the screen permanently `Partial`, which is
 * the honest reading: `Live` claims everything on the screen was read.
 *
 * # Read-only, deliberately
 *
 * Acknowledge, propose-closure, withdraw-closure and approve-closure are POSTs
 * and are NOT wired (go-wallet-admin-client#23). The maker-checker block still
 * renders both slots — law 15 says the second signature must be legible before
 * it is met — with both controls disabled and the reason stated.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_aml.go json tags
// ---------------------------------------------------------------------------

/**
 * One queue row, as `amlCaseView` serialises it.
 *
 * Only `closure_proposed_by` is `omitempty` on the Go side; everything else is
 * always on the wire. The reader below still treats every field as possibly
 * absent, because "the handler always sends it" is a property of the handler we
 * are looking at today and this console must not crash on the one it is
 * deployed against tomorrow.
 */
export interface AMLCaseView {
  readonly case_ref: string
  readonly customer_id: number
  /** `open | escalated | closure_proposed | closed`. Read fail-closed. */
  readonly state: string
  readonly reason_code: string
  readonly opened_by: string
  readonly opened_at: string
  /** Who is waiting on a second pair of eyes. Absent unless a closure is proposed. */
  readonly closure_proposed_by?: string
  /** The backend's own judgement: `open` or `closure_proposed`. */
  readonly needs_a_human: boolean
}

export interface AMLQueueResponse {
  /** Depth of the WHOLE queue, not of this page. A Go int64. */
  readonly total?: number
  /**
   * Typed `unknown[]` on purpose. The rows are the only part of this payload a
   * loop dereferences, and a loader that throws on a malformed row breaks the
   * contract that it resolves for every answer the backend gives. Narrowing
   * each row explicitly is what makes that impossible rather than unlikely.
   */
  readonly cases: readonly unknown[]
}

export const AML_QUEUE_PATH = '/aml/cases'

const SOURCE = 'postgres · aml_cases (GET /v1/admin/aml/cases)'

/**
 * Our own operational record, and authoritative for this.
 *
 * Unlike a balance, a case state has no institution-side counterpart to lose a
 * discrepancy to: first-line AML handling is ours entirely and the institution
 * decides only the outcome we escalate. So `operational` is the whole truth
 * here, not one side of a comparison.
 */
const AUTHORITY = 'operational'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

/** A string we were actually sent, or the empty string. Never `undefined` downstream. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a `total` or a
 * `customer_id` past 2^53 has already lost digits by the time it reaches here.
 * Printing it anyway gives a number that is wrong in its last places and looks
 * entirely plausible — and a customer id that is plausibly wrong is one an
 * operator will look up, find, and act on the wrong account for.
 */
function exactInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Returns the raw string unchanged rather than inventing one if the backend
 * sends something unparseable: on a queue sorted oldest-first, a wrong "raised"
 * time is worse than an ugly one, because it is the only ageing signal the row
 * carries.
 */
function stamp(iso: string): string {
  if (iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/**
 * The whole-queue depth, or nothing.
 *
 * A negative depth is not a depth. `count(*)` cannot produce one, so a negative
 * is shape drift, and "-3 awaiting a human" is a figure an operator can neither
 * act on nor recognise as broken — it takes the same unreadable path as a value
 * past 2^53.
 */
function depthOf(value: unknown): number | null {
  const n = exactInteger(value)
  return n === null || n < 0 ? null : n
}

/** A row after narrowing: every field present, absence expressed as `''`/`null`. */
interface CaseRow {
  readonly ref: string
  readonly customerId: number | null
  /** The raw wire value, kept so an unknown state can be shown verbatim. */
  readonly state: string
  readonly reasonCode: string
  readonly openedBy: string
  readonly openedAt: string
  readonly proposedBy: string
  /**
   * `needs_a_human` as three states: the backend said yes, said no, or did not
   * say anything this console can read.
   *
   * Kept alongside `needsAHuman` rather than instead of it because the two
   * questions are different, and each surface asks only one of them. The queue
   * asks "may I stop paying attention to this row?" and answers no on anything
   * it could not read. A case view asks "what did the backend say?", and there
   * the third state has to survive: rendering an unread field as `false` is the
   * one answer that empties a compliance screen (law 15), and rendering it as
   * `true` invents a claim nobody made.
   */
  readonly needsAHumanStated: boolean | null
  readonly needsAHuman: boolean
}

/**
 * One wire row, or `null` if it is not a row at all.
 *
 * `needs_a_human` is read as "anything that is not literally `false`". The
 * backend computes it as `open || closure_proposed`, and it is the one field
 * that can only ever ADD attention here — so a missing or misshapen value
 * resolves to "yes, someone still has to look", which is the reading that shows
 * less confidence. The inverse default would let a shape change silently empty
 * a compliance queue.
 */
function readCase(raw: unknown): CaseRow | null {
  // `typeof [] === 'object'`, so the array has to be refused explicitly. Without
  // it a `[]` on the wire narrows to a row with every field absent and is
  // COUNTED AS READ — a ghost row of dashes that inflates "on this page" and
  // that the unreadable count never mentions. An array is not a case.
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const c = raw as Partial<AMLCaseView>
  const stated = typeof c.needs_a_human === 'boolean' ? c.needs_a_human : null
  return {
    ref: text(c.case_ref),
    customerId: exactInteger(c.customer_id),
    state: text(c.state),
    reasonCode: text(c.reason_code),
    openedBy: text(c.opened_by),
    openedAt: text(c.opened_at),
    proposedBy: text(c.closure_proposed_by),
    needsAHumanStated: stated,
    // Identical to the `!== false` this used to be, and deliberately so: a
    // value the console could not read reaches the queue as "yes, still work".
    needsAHuman: stated !== false,
  }
}

// ---------------------------------------------------------------------------
// The case-state machine, as the operator reads it
// ---------------------------------------------------------------------------

/**
 * What each state says on the chip.
 *
 * `closure_proposed` spells out the negative because the positive half of the
 * sentence is what a scanning eye takes: "closure proposed" alone reads as
 * done. The state means one operator has ASKED to close and nothing has
 * happened — the case is still open in every sense that matters.
 */
const STATE_LABEL: Readonly<Record<string, string>> = {
  open: 'open · being worked',
  escalated: 'with the institution',
  closure_proposed: 'closure proposed · NOT closed',
  closed: 'closed · two signatures',
}

/**
 * Severity, which is not the same as approval.
 *
 * `closed` is `n`, not `g`. A closed AML case is finished, not good news, and
 * green on the terminal state of a compliance queue teaches an operator to
 * scan for colour rather than to read. `escalated` is `n` for the same reason:
 * it is with the institution and is not our work, which is neither reassuring
 * nor alarming.
 */
const STATE_TONE: Readonly<Record<string, Tone>> = {
  open: 'a',
  escalated: 'n',
  closure_proposed: 'a',
  closed: 'n',
}

/**
 * The state chip for one case.
 *
 * Two sources for the same judgement, and the backend's `needs_a_human` wins in
 * one direction only: it can take calm away and can never grant it. That covers
 * the case this console is most exposed to — a fifth state added to the backend
 * after this file was written. An unknown state is critical and says so, rather
 * than resolving to the nearest thing it looks like.
 */
function stateChip(c: CaseRow): ChipCell {
  const known = Object.prototype.hasOwnProperty.call(STATE_TONE, c.state)
  if (!known) {
    const shown = c.state === '' ? 'state not returned' : `${c.state} · unknown to this console`
    return [shown, 'r']
  }
  const mapped: Tone = STATE_TONE[c.state] ?? 'r'
  const tone: Tone = c.needsAHuman && (mapped === 'n' || mapped === 'g') ? 'a' : mapped
  return [STATE_LABEL[c.state] ?? c.state, tone]
}

/** Whether this row is the one thing on the screen that needs a second operator. */
function awaitsChecker(c: CaseRow): boolean {
  return c.state === 'closure_proposed'
}

// ---------------------------------------------------------------------------
// The queue table
// ---------------------------------------------------------------------------

/**
 * Seven columns, and not the corpus's seven.
 *
 * Value and SLA are gone (see the header); in their place are the two
 * attributions the maker-checker rule turns on. `opened_by` and
 * `closure_proposed_by` are the two people who may NOT approve a closure
 * (`ErrOpenerCannotApprove`, `ErrSameOperator`), so an operator scanning the
 * queue can see which cases they personally cannot sign off before opening one.
 * That is the reason the backend puts the proposer on the queue row at all.
 */
const COLS: readonly Column[] = [
  ['Case', 'm', '104px'],
  ['Customer id', 'm', '108px'],
  ['Reason code', 'x', 'minmax(170px,1.2fr)'],
  ['Raised', 'm', '150px'],
  ['Raised by', 'x', 'minmax(130px,1fr)'],
  ['Closure proposed by', 'x', 'minmax(150px,1fr)'],
  ['State', 'c', '240px'],
]

/**
 * The proposer cell.
 *
 * A case in `closure_proposed` with no named proposer is a contradiction, and
 * the safe side of it is to say so: without the name, this console cannot tell
 * an operator whether they are the maker, and "the maker may not be the
 * checker" stops being enforceable at the point where a human decides to click.
 * An empty proposer on any other state is simply the field not applying.
 */
function proposerCell(c: CaseRow): string {
  if (c.proposedBy !== '') return c.proposedBy
  return awaitsChecker(c) ? '— not recorded' : '—'
}

function row(c: CaseRow): readonly Cell[] {
  return [
    c.ref === '' ? '— no reference' : c.ref,
    c.customerId === null ? '—' : String(c.customerId),
    c.reasonCode === '' ? '— not recorded' : c.reasonCode,
    // Never blank. A blank cell in a timestamp column reads as "just now" to
    // the same glance that reads a blank count as zero.
    stamp(c.openedAt) === '' ? '— not returned' : stamp(c.openedAt),
    c.openedBy === '' ? '— unattributed' : c.openedBy,
    proposerCell(c),
    stateChip(c),
  ]
}

/**
 * Whether the reported depth is contradicted by the page beneath it.
 *
 * `Queue` and `QueueDepth` are two separate statements in the handler with no
 * transaction around them, so a case approved or escalated between the two
 * reads returns a page LONGER than the depth — including the case where the
 * depth comes back 0 and the page is not empty. That is not a rounding
 * difference to smooth over: the depth is the figure the operator reads as "how
 * much work is left", and a zero of it beside a table of open cases is the most
 * consequential thing this screen can get wrong.
 */
function depthContradicted(total: number | null, arrived: number): boolean {
  return total !== null && arrived > total
}

/**
 * The count under the table.
 *
 * Three separate facts, and they are not interchangeable: how many rows this
 * page drew, how deep the whole queue is, and how many rows arrived unreadable.
 * Collapsing them into one number is how a truncated page reads as a complete
 * queue.
 */
function footerLine(shown: number, total: number | null, unreadable: number): string {
  const arrived = shown + unreadable
  const head = total === null
    ? `${String(shown)} case(s) shown · the depth of the whole queue could not be read, which is not a depth of zero`
    : depthContradicted(total, arrived)
      ? `${String(shown)} case(s) shown · the backend reported a whole-queue depth of ${String(total)}, which is fewer than this page returned — the two disagree, and neither is stated here as the queue`
      : shown === total
        ? `${String(shown)} case(s) awaiting a human`
        : `${String(shown)} shown of ${String(total)} awaiting a human · this page is capped at 200`
  return unreadable === 0 ? head : `${head} · ${String(unreadable)} row(s) could not be read`
}

function queueTable(rows: readonly CaseRow[], total: number | null, unreadable: number): TableBlock {
  return {
    t: 'table',
    // Cases, not escalations. `GET /v1/admin/aml/cases` is unfiltered and this
    // loader sends no state, so every case is here — escalated or not.
    title: 'AML cases',
    // Not the journal, but of the three the block language offers this is the
    // one that means "our own record, read now". `provider` would attribute our
    // case state to the institution, which inverts the whole design — we
    // handle, they decide — and `analytics` would be a law-12 lie.
    src: 'ledger',
    // No filter was applied, so no facet bar. `false` suppresses it outright
    // rather than drawing an empty one that implies a filter exists.
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: footerLine(rows.length, total, unreadable),
    foot2:
      'No amount, SLA target or customer name is shown, because this read returns none — an invented figure in a compliance column is worse than an absent column. A proposed closure is not a closed case.',
  }
}

// ---------------------------------------------------------------------------
// What the queue as a whole says
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Maker-checker, one block per proposal
// ---------------------------------------------------------------------------

/**
 * Who is barred from countersigning this closure, in words.
 *
 * Both refusals are real backend errors — `ErrSameOperator` for the proposer
 * and `ErrOpenerCannotApprove` for whoever raised the case — so naming them
 * before the click is the difference between a control that teaches and one
 * that only rejects. Where a name is missing the sentence says the console
 * cannot answer, rather than listing one name and implying it is the whole bar.
 */
function barredSentence(c: CaseRow, tail: string): string {
  if (c.proposedBy !== '' && c.openedBy !== '') {
    return c.proposedBy === c.openedBy
      ? `Refused for ${c.proposedBy}, who both raised this case and proposed the closure. It needs a second Compliance session.${tail}`
      : `Refused for ${c.proposedBy}, who proposed the closure, and for ${c.openedBy}, who raised the case. It needs a third Compliance session.${tail}`
  }
  if (c.openedBy !== '') {
    return `Refused for ${c.openedBy}, who raised the case. The proposer is not named on this read, so this console cannot say who else is barred.${tail}`
  }
  if (c.proposedBy !== '') {
    return `Refused for ${c.proposedBy}, who proposed the closure. Whoever raised the case is not named on this read, so this console cannot say who else is barred.${tail}`
  }
  return `Neither the proposer nor whoever raised this case is named on this read, so this console cannot say who is barred from approving.${tail}`
}

/**
 * What the surface drawing the approval can and cannot say for itself.
 *
 * Two facts differ between the queue and a single case, and both of them are
 * sentences an operator reads rather than fields they scan, so neither may be
 * hardcoded to the queue's answer:
 *
 *   - `proposalAt` — the queue read carries no event times at all, and the case
 *     read carries the whole trail. Empty means "this read did not establish
 *     it", which is not the same as "it did not happen".
 *   - `submitsNothing` — both surfaces submit nothing, and they are not the
 *     same thing, so they may not claim to be. Neither may say "this screen"
 *     when it is not one.
 */
interface ApprovalContext {
  /** The stamp of the proposal, or `''` when this read did not establish one. */
  readonly proposalAt: string
  /** How this surface states that it sends no instruction. Leading space. */
  readonly submitsNothing: string
  /**
   * Why the OPERATOR READING THIS may not countersign, or `''` when they may.
   *
   * The third fact that differs between the two surfaces, and the one law 15
   * actually turns on. `barredSentence` says who is barred in general — a
   * statement about the case. This says whether the person looking at it is one
   * of them, which is a statement about them, and it is the one that decides
   * whether the slot is a live control or a stated refusal.
   *
   * Empty on a surface that cannot submit at all, where the slot is dead for a
   * reason that has nothing to do with who is reading.
   */
  readonly refusal: string
  /** Whether this surface has a countersign control to point at. */
  readonly wired: boolean
}

/**
 * The maker slot.
 *
 * Filled reads `n`, not the corpus's `g`. Green on the maker half of a CLOSURE
 * says the closure is going well, and a proposal to close an AML case with no
 * action is precisely the step that should read as pending scrutiny rather than
 * as progress. An unnamed maker is `r`: a maker-checker control that cannot
 * name the maker cannot enforce that the maker is not the checker.
 */
function makerSlot(c: CaseRow, ctx: ApprovalContext): AprSlot {
  const when = ctx.proposalAt === '' ? '—' : ctx.proposalAt
  // Never "not yet" and never blank. A proposal whose time this read did not
  // establish is a proposal whose age nobody knows, and age is the only thing
  // that distinguishes a countersignature that is pending from one that has
  // been sitting unlooked-at for a fortnight.
  const timing = ctx.proposalAt === ''
    ? 'The time of the proposal is not on this read.'
    : `Proposed at ${ctx.proposalAt}, read from the case trail.`
  if (c.proposedBy === '') {
    return ['Maker', 'unattributed', 'r', '— not recorded', when,
      'The case is in closure_proposed and this read did not name who proposed it. Until it does, no operator can be told whether they are the maker.',
      'Proposed', true, 'r']
  }
  return ['Maker', 'proposed', 'n', c.proposedBy, when,
    `Proposed closing with no action. The proposal has had no effect: the case is still open, still watched, and still in the queue. ${timing}`,
    'Proposed', true, 'n']
}

/**
 * The checker slot, which is where law 15 stops being a description.
 *
 * Three renderings, and the difference between them is the whole control:
 *
 *   - **the reading operator is barred** — the slot names WHY, in the second
 *     person, and the countersign control below is dead for the same reason.
 *     This is the case the rule exists for, and the sentence has to arrive here
 *     rather than at a 409 after somebody has composed their reasoning.
 *   - **they may sign, and this surface can submit** — the slot points at the
 *     control. It does not become the control: one write path, and a button
 *     inside an approval card that posted would be a second one.
 *   - **this surface submits nothing** — dead, and honest about which of the
 *     two reasons applies.
 *
 * The slot's own `disabled` flag stays `true` in every branch. It is a display
 * of the standing, not an affordance, and `Apr` now renders that as a genuinely
 * disabled button rather than a live-looking one that ignores the click.
 */
function checkerSlot(c: CaseRow, ctx: ApprovalContext): AprSlot {
  if (ctx.refusal !== '') {
    return ['Checker', 'barred', 'r', 'not you', '—',
      `${ctx.refusal} ${barredSentence(c, '')}`,
      'Countersign — barred for you', true, 'r']
  }
  if (ctx.wired) {
    return ['Checker', 'awaiting', 'a', 'unassigned', '—',
      `${barredSentence(c, '')} Nothing on this read bars you: the countersign control is below, and it will ask you to type the case reference.`,
      'Countersign — the control is below', true, 'a']
  }
  return ['Checker', 'awaiting', 'a', 'unassigned', '—',
    barredSentence(c, ctx.submitsNothing),
    'Countersign — not available on this screen', true, 'a']
}

/**
 * One proposed closure, as the two slots law 15 requires.
 *
 * Both slots render from the first read so the second signature is an
 * expectation rather than a surprise, and both stay display-only: the act
 * itself is a `form` below, so there is exactly one path from this console to a
 * POST. There is no Value-at-rest line in the summary: the corpus draws one and
 * this endpoint returns no amounts, and a number invented to fill the row would
 * be a fabricated figure in the middle of an approval.
 */
function closureApproval(c: CaseRow, ctx: ApprovalContext): AprBlock {
  const ref = c.ref === '' ? '— no reference' : c.ref
  return {
    t: 'apr',
    title: `Closure awaiting a second approver — case ${ref}`,
    summary: [
      ['Action', 'Close with no action', ''],
      ['Case', ref, ''],
      ['Customer id', c.customerId === null ? '—' : String(c.customerId), ''],
      ['Reason code', c.reasonCode === '' ? '— not recorded' : c.reasonCode, ''],
    ],
    slots: [
      makerSlot(c, ctx),
      checkerSlot(c, ctx),
    ],
    note2:
      'Never a single Approve button. A proposal is not a closure — nothing has happened to this case, and it stays in the queue until a second operator signs. Proposing, withdrawing and approving are three separate calls. No value at rest is shown because this read returns no amounts.',
  }
}

/**
 * The queue's own answers to the two questions the approval block asks.
 *
 * No proposal time, because this read carries no event times; and "this screen
 * reads the queue" because that is exactly what it is.
 */
const QUEUE_APPROVAL: Omit<ApprovalContext, 'refusal'> = {
  proposalAt: '',
  // The queue lists proposals; it does not open them. Countersigning asks the
  // operator to type the case reference to confirm, and a control that did that
  // from a list would be one mistyped row away from closing the wrong case.
  wired: false,
  submitsNothing: ' Countersigning happens on the case itself — select the row to open it.',
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

function emptyQueueNote(total: number | null): NoteBlock {
  return total === 0
    ? {
        t: 'note',
        tone: 'g',
        kicker: 'Queue',
        title: 'Nothing is waiting for a human',
        body: 'No case is open and none has a closure awaiting a countersignature. This is a counted zero, not a failed read — and it is a statement about this queue, not about every AML case: a case the institution is holding, or one already closed, is not on it.',
        ref: 'go-wallet#82 · the escalation queue is cases still needing a human',
      }
    : {
        t: 'note',
        tone: 'a',
        kicker: 'Queue',
        title: 'This page returned no case, and the queue depth is unknown',
        body: 'No row arrived, and the backend did not send a depth this console can state exactly. An empty page with an unreadable depth is not an empty queue — it is a read that answered for neither.',
        ref: 'A missing count is not a count of zero.',
      }
}

function unreadableRowsNote(unreadable: number): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Not shown',
    title: `${String(unreadable)} row(s) on this page could not be read`,
    body: 'The backend sent entries this console could not read as cases. They are counted rather than dropped, because a case missing from a queue is a case nobody works — and the table above is shorter than the queue by exactly this many.',
    ref: 'An unread row is not an absent case.',
  }
}

/**
 * The absence of any pending closure — which is only sayable when every row was
 * read.
 *
 * "Nothing is waiting for a second approver" is the most reassuring sentence on
 * this screen, and the maker-checker rule is the one control it can defeat. So
 * it is stated only over a page this console read in full: with even one
 * unreadable row the honest note is that the question could not be answered,
 * because the proposal that goes uncountersigned is exactly the one hiding in a
 * row nobody could parse.
 */
function noProposalsNote(unreadable: number): NoteBlock {
  if (unreadable > 0) {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Maker-checker',
      title: 'Whether a closure is awaiting a second approver could not be established',
      body: `No row this console could read carries a proposed closure — but ${String(unreadable)} row(s) on this page could not be read at all, so this is not "none waiting". A proposal sitting in an unread row is a closure nobody countersigns, which is the burial the maker-checker rule exists to prevent.`,
      ref: 'law 15 · a closure needs a maker and a different checker',
    }
  }
  return {
    t: 'note',
    tone: 'n',
    kicker: 'Maker-checker',
    title: 'No closure is awaiting a second approver',
    body: 'Every case on this page is being worked; none has a proposed closure sitting for a countersignature. Closing a case with no action is the one direction that needs two operators, and nothing is currently in it.',
    ref: 'law 15 · a closure needs a maker and a different checker',
  }
}

/**
 * What the operator is not being told.
 *
 * The first three entries are unconditional and are the reason this screen is
 * permanently `Partial`. They are not read failures — the endpoint was never
 * asked for them — but they ARE parts of the designed screen that are absent,
 * and `Live` is a claim that everything on the screen was read. The per-row
 * entries are collapsed by kind rather than listed one per case, so a hundred
 * unattributed rows produce a sentence rather than a hundred.
 */
function missingFrom(rows: readonly CaseRow[], total: number | null, unreadable: number): string[] {
  const missing = [
    'per-case value at rest',
    'per-case SLA posture',
    'customer names (ids only)',
  ]
  if (total === null) missing.push('the depth of the whole queue')
  else if (depthContradicted(total, rows.length + unreadable)) {
    missing.push('the depth of the whole queue (the reported depth is smaller than this page)')
  }
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} unreadable row(s)`)
    // Named separately from the row count because it is a different thing the
    // operator was not told: not "some rows are missing" but "the maker-checker
    // question was not answered for them".
    missing.push('whether the unreadable row(s) carry a closure awaiting a countersignature')
  }

  const noRef = rows.filter((c) => c.ref === '').length
  if (noRef > 0) missing.push(`${String(noRef)} case(s) with no reference`)

  const noOpener = rows.filter((c) => c.openedBy === '').length
  if (noOpener > 0) missing.push(`${String(noOpener)} case(s) with no named opener`)

  const noProposer = rows.filter((c) => awaitsChecker(c) && c.proposedBy === '').length
  if (noProposer > 0) {
    missing.push(`${String(noProposer)} proposed closure(s) with no named proposer`)
  }

  const unknownStates = [...new Set(
    rows.filter((c) => !Object.prototype.hasOwnProperty.call(STATE_TONE, c.state)).map((c) => c.state),
  )]
  if (unknownStates.length > 0) {
    missing.push(`the meaning of state(s) ${unknownStates.map((s) => s === '' ? '(empty)' : s).join(', ')}`)
  }
  return missing
}

/**
 * Folds the queue response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every absence path is table-testable
 * without a client (CLAUDE.md Part III).
 *
 * `readAt` is a parameter rather than an ambient `new Date()` for the same
 * reason the backend's decision functions take `now`. Note that it is the
 * CONSOLE'S clock, not the server's: unlike the dashboard endpoint this one
 * sends no `generated_at`, so the honest stamp available is the moment the
 * console read it. The alternative — an empty `readAt` — loses, because the
 * badge would then be unable to tell an operator how old the page in front of
 * them is, and a stale queue is the failure this screen exists to catch.
 */
export function composeAMLQueue(
  res: AMLQueueResponse, readAt: Date, caller: Caller, api: ApiClient,
): ScreenData {
  const rows: CaseRow[] = []
  let unreadable = 0
  for (const raw of res.cases) {
    const parsed = readCase(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const total = depthOf(res.total)
  const proposals = rows.filter(awaitsChecker)

  const blocks: Block[] = [queueTable(rows, total, unreadable)]

  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyQueueNote(total))

  if (proposals.length === 0) blocks.push(noProposalsNote(unreadable))
  // One block per proposal, and never truncated. A proposal left off the screen
  // to keep it short is a proposal nobody countersigns, which is the same
  // burial the maker-checker rule prevents by a different route.
  //
  // Each carries the READER's own standing, computed here rather than on the
  // case, because the backend puts `closure_proposed_by` on the queue row for
  // exactly this: "an operator scanning the queue needs to know which cases
  // they personally cannot approve before they open one."
  else {
    for (const c of proposals) {
      blocks.push(closureApproval(c, {
        ...QUEUE_APPROVAL,
        refusal: closureApprovalRefusal(caller, { openedBy: c.openedBy, proposedBy: c.proposedBy }),
      }))
    }
  }

  // Raising a case is the queue's own act — it names no case, so it belongs
  // with the list rather than inside one of them.
  blocks.push(openCaseForm(api, caller))

  const missing = missingFrom(rows, total, unreadable)
  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: AUTHORITY,
    readAt: readAtStamp(readAt),
    missing,
  }
  // What each row of the table above IS, so selecting one can open the case it
  // names rather than the string its first cell happens to render. A case whose
  // `case_ref` did not arrive is `null` — it draws as `— no reference` and it
  // opens into nothing, which is the same fact stated twice rather than a row
  // that looks openable and fetches a reference nobody has.
  const rowKeys = rows.map((c) => (c.ref === '' ? null : c.ref))
  return {
    blocks,
    provenance,
    rowKeys,
    /**
     * What this screen is, which is NOT what the rail calls it.
     *
     * The rail says "AML escalations" and this read is `GET /aml/cases`,
     * unfiltered, with no `state` sent — so every case is here whether or not
     * anybody escalated it. Escalation is one thing that can HAPPEN to a case
     * (`POST /aml/cases/{ref}/escalate`, and `GET /aml/escalations` is its own
     * read that nothing in this console consumes); it is not what this queue
     * selects on.
     *
     * The consequence of the old name is specific: a compliance officer who
     * reads "escalations" and sees four rows concludes four cases have gone to
     * the institution. The truth may be that four cases exist and none has.
     *
     * `NAV` is deliberately not edited — it is the design contract, and
     * `reachable.ts` says so in terms. The screen states what it is actually
     * showing, which is what `ScreenData.title` is for. `sf-failed` already
     * takes exactly this shape for exactly this reason.
     */
    title: 'AML cases',
    purpose:
      'Every AML case, escalated or not — this read sends no state filter. '
      + 'Escalation is something that happens to a case and is recorded on it; '
      + 'it is not what puts a case on this queue.',
  }
}

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. Falling back to the design corpus would put four
 * invented AML cases, an invented SLA breach and an invented maker-checker
 * signature on a compliance screen — and a fabricated empty queue is no better,
 * because "no case needs a human" is the single most consequential sentence
 * this screen can say.
 *
 * `readAt` is empty on purpose: nothing was read, and stamping the attempt with
 * the browser's clock would date a queue that does not exist.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null
    ? 'No case on this screen was read. This is not an empty queue.'
    : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'AML escalations',
      title: 'This screen could not be read',
      body: `${reason} No case, no state and no pending closure below is known — that is different from the queue being empty, and different again from nothing awaiting a second approver.`,
      ref,
    }],
    provenance: {
      kind: 'partial',
      source: SOURCE,
      authority: AUTHORITY,
      readAt: '',
      missing: ['every case on the AML escalation queue'],
    },
  }
}

function isAMLQueueResponse(payload: unknown): payload is AMLQueueResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<AMLQueueResponse>).cases)
}

/**
 * Reads the AML escalation queue.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said
 * "I don't know" is a `partial` the operator can read, not an exception that
 * collapses the screen. A refusal keeps its `decision_ref` (law 13) — this
 * queue is Compliance-only, so a 403 is the expected answer for an Ops session
 * and it must arrive with the reference that explains it.
 */
export const loadAMLQueue: ScreenLoader = async (api) => {
  // Both reads start together and the queue is awaited first. `callerBeside`
  // is what keeps the session promise from being left dangling when the queue
  // read throws — see its comment; the failure mode is an unhandled rejection
  // on every cancelled navigation.
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(AML_QUEUE_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The AML queue endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    // An AbortError is a navigation away, not a failure to report. Let it
    // propagate so the caller's cancellation stays a cancellation.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isAMLQueueResponse(payload)) {
    return unreadableScreen('The AML queue endpoint returned a shape this console does not know.', null)
  }
  return composeAMLQueue(payload, new Date(), await calling, api)
}

// ===========================================================================
// ONE CASE, AND ITS TRAIL — GET /v1/admin/aml/cases/{ref}
// ===========================================================================

/**
 * One AML escalation, read back from `GET /v1/admin/aml/cases/{ref}`
 * (go-wallet#82, consumed by #46).
 *
 * # Why this lives beside the queue and not in a file of its own
 *
 * `decision.ts` is the precedent for the SHAPE — a read that hangs off a row
 * rather than off a screen, absent from `SCREEN_LOADERS` — and this follows it.
 * It is not the precedent for the LOCATION, and the difference matters: there
 * was no decisions module for `readDecision` to join, and there is an AML
 * module here.
 *
 * The case read and the queue read narrow the same `amlCaseView` with the same
 * reader, name the same four states with the same words and the same tones, and
 * draw the same two maker-checker slots. Split across two files, each of those
 * is a copy that drifts — and the drift is not cosmetic. An operator clicks a
 * row that says `closure proposed · NOT closed` and lands on a case; if the two
 * files disagree about what that state means, one of them is the version that
 * reads as resolved, and burying a proposed closure is precisely the failure
 * the maker-checker rule exists to prevent. One `STATE_LABEL`, one `readCase`,
 * one `closureApproval` makes disagreeing impossible rather than unlikely.
 *
 * # The trail is the reason to open a case at all
 *
 * The queue row already carries the case's fields. What it cannot carry is
 * `aml_case_events` — who raised it, who escalated, who proposed a closure and
 * on what note. That table is append-only in the database (a row-level trigger
 * refuses UPDATE and DELETE, a statement-level one refuses TRUNCATE), and
 * `aml_cases.state` is a projection of it. So the trail is the record and the
 * state is a summary of the record, which is why this view renders both and
 * says when they disagree instead of preferring one.
 *
 * Rendered in the order the backend returned, with each event's `seq` beside
 * it. Never re-sorted: sorting would make a trail that arrived out of order
 * look correct, and the console would have hidden the one fact worth reporting
 * about an append-only table. Gaps and disorder are stated, not repaired.
 *
 * # Three ways to have no trail, and they are three different sentences
 *
 *   - **empty** — `history: []`. The backend read the case and found no event.
 *   - **unreadable in part** — some entries arrived and could not be read as
 *     events. They are COUNTED. Folding them into the rows that did parse would
 *     produce a trail shorter than the record with nothing saying so.
 *   - **not read at all** — the endpoint refused, failed or does not exist.
 *
 * # The acts, and the one that is not here
 *
 * Six of the seven case routes are drawn below the trail (#145): note,
 * escalate, acknowledge, resolve, propose closure, withdraw closure and
 * approve closure. Raising a case is the seventh and it is NOT here — it names
 * no case, so it belongs on the queue.
 *
 * Every one of them renders whatever the case's state, disabled where the state
 * refuses it, with the domain's own sentence. Hiding them would make a case
 * look like it had fewer options than it has, and on a compliance queue the
 * difference between "not available yet" and "not possible" is the difference
 * between waiting and escalating.
 *
 * The approval block above them stays display-only in every branch. The act is
 * a `form`, so there is exactly one path from this console to a POST — and law
 * 15's two slots stay what they are for, which is making the second signature
 * legible before it is met rather than being the thing that collects it.
 */

/** One entry in the trail, as `amlEventView` serialises it. */
export interface AMLEventView {
  /** Per-case sequence, total and stable. `UNIQUE (case_id, seq)`. */
  readonly seq: number
  /** One of seven, CHECK-constrained in the database. Read fail-closed. */
  readonly action: string
  readonly actor: string
  readonly note: string
  readonly at: string
}

/**
 * `amlCaseDetailView`.
 *
 * Go embeds `amlCaseView`, so the case's own fields are flattened alongside
 * `history` rather than nested. `history` is `unknown[]` for the same reason
 * the queue's `cases` is: a loop dereferences it, and narrowing every entry
 * explicitly is what keeps a malformed one from throwing out of a read that is
 * contractually forbidden to throw.
 */
export interface AMLCaseDetailView extends AMLCaseView {
  readonly history: readonly unknown[]
}

/**
 * The path for one case. The client adds the `/v1/admin` prefix.
 *
 * Built from `AML_QUEUE_PATH` rather than spelled again, so the queue and the
 * case cannot end up addressing two different endpoints. Encoded because a
 * `case_ref` is backend-shaped (`CITEXT`, operator-quotable) and one carrying a
 * slash or a `?` would otherwise address something else entirely — and this
 * read would then report on whatever answered.
 */
export function amlCasePath(ref: string): string {
  return `${AML_QUEUE_PATH}/${encodeURIComponent(ref)}`
}

const CASE_SOURCE = 'postgres · aml_cases + aml_case_events (GET /v1/admin/aml/cases/{ref})'

// ---------------------------------------------------------------------------
// Reading the trail
// ---------------------------------------------------------------------------

/** One event after narrowing. Absence is `''`/`null`, never a substituted value. */
interface EventRow {
  readonly seq: number | null
  readonly action: string
  readonly actor: string
  readonly note: string
  readonly at: string
}

/**
 * One wire event, or `null` if it is not an event at all.
 *
 * Arrays are refused explicitly for the reason `readCase` refuses them: a `[]`
 * narrows to an object with every field absent and would be counted as a row
 * that was read — a blank line in an audit trail, which is worse than a stated
 * absence because nothing about it says it is one.
 */
function readEvent(raw: unknown): EventRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const e = raw as Partial<AMLEventView>
  return {
    seq: exactInteger(e.seq),
    action: text(e.action),
    actor: text(e.actor),
    note: text(e.note),
    at: text(e.at),
  }
}

/**
 * The event's timestamp, only when it is one.
 *
 * `null` for absent, unparseable, or dated before 2000. The last of those is
 * not paranoia: `omitempty` does nothing to a Go `time.Time`, so an unset
 * `at` serialises as `0001-01-01T00:00:00Z` and parses perfectly. On a trail
 * that is the worst possible shape of wrong — it reads as a real time, and it
 * sorts before every genuine event, so the first thing that happened to the
 * case appears to be whichever row failed to get a clock.
 */
function properStamp(iso: string): string | null {
  if (iso === '') return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  if (at.getUTCFullYear() < 2000) return null
  return stamp(iso)
}

/**
 * The timestamp cell, which is never blank.
 *
 * Delegates the formatting to `stamp` so the trail and the queue cannot draw
 * the same instant two ways. The extra judgement is the zero date, and it lives
 * here rather than in `stamp` because it is the trail that needs it: the queue
 * sorts server-side on `opened_at`, and this view is the only place ordering is
 * a claim the console makes.
 */
function eventAt(iso: string): string {
  const good = properStamp(iso)
  if (good !== null) return good
  if (iso === '') return '— not returned'
  return Number.isNaN(new Date(iso).getTime())
    ? `${iso} — a timestamp this console cannot read`
    : `${iso} — a zero timestamp, so the event is not dated`
}

/**
 * What each action did, in the words a compliance reviewer reads six months on.
 *
 * The two closure entries spell out their effect because the verbs alone invert
 * it. "Closure proposed" reads as a case being wrapped up and means nothing has
 * happened; "closure withdrawn" reads as a step backwards and means the case
 * returned to being worked, which is the safe direction.
 */
const ACTION_LABEL: Readonly<Record<string, string>> = {
  opened: 'raised',
  note_added: 'note added',
  escalated: 'escalated to the institution',
  acknowledged_by_institution: 'acknowledged by the institution',
  closure_proposed: 'closure proposed · nothing closed',
  closure_approved: 'closure approved · two signatures',
  closure_withdrawn: 'closure withdrawn · back to open',
}

/**
 * Severity on a trail, which is mostly none.
 *
 * A history is a record, and toning every line of it teaches an operator to
 * scan for colour instead of reading. So everything that happened normally is
 * `n` — including `closure_approved`, for the reason `closed` is `n` on the
 * queue: a closed AML case is finished, not good news. `closure_proposed` is
 * the single `a`, because it is the one entry that means somebody still has to
 * act. No entry is ever `g`.
 */
const ACTION_TONE: Readonly<Record<string, Tone>> = {
  opened: 'n',
  note_added: 'n',
  escalated: 'n',
  acknowledged_by_institution: 'n',
  closure_proposed: 'a',
  closure_approved: 'n',
  closure_withdrawn: 'n',
}

/**
 * The action chip.
 *
 * An action outside the seven is `r` and shown verbatim, the same posture
 * `stateChip` takes to a fifth state. The database CHECK-constrains the column
 * to seven values today; an eighth reaching this console means the backend
 * grew a step this view does not understand, and guessing which of the seven it
 * resembles is how a closure gets rendered as a note.
 */
function actionChip(e: EventRow): ChipCell {
  const known = Object.prototype.hasOwnProperty.call(ACTION_TONE, e.action)
  if (!known) {
    return [e.action === '' ? 'action not returned' : `${e.action} · unknown to this console`, 'r']
  }
  return [ACTION_LABEL[e.action] ?? e.action, ACTION_TONE[e.action] ?? 'r']
}

const TRAIL_COLS: readonly Column[] = [
  ['Seq', 'n', '64px'],
  ['When', 'm', '150px'],
  ['Action', 'c', '250px'],
  ['Actor', 'x', 'minmax(140px,1fr)'],
  ['Note', 'x', 'minmax(220px,1.8fr)'],
]

function eventCells(e: EventRow): readonly Cell[] {
  return [
    // Never blank and never renumbered. The seq is how an operator sees that
    // the trail in front of them is the whole trail.
    e.seq === null ? '— not numbered' : String(e.seq),
    eventAt(e.at),
    actionChip(e),
    // Invariant 6 / the events table's own CHECK: every action is attributable.
    // A blank actor here is that constraint failing, not a system event.
    e.actor === '' ? '— unattributed' : e.actor,
    e.note === '' ? '— no note' : e.note,
  ]
}

// ---------------------------------------------------------------------------
// What the trail says about itself
// ---------------------------------------------------------------------------

/**
 * The things a trail can be wrong about, counted rather than corrected.
 *
 * Each one is separately reportable because each one has a different cause and
 * a different remedy, and because a single "the trail looks odd" would be
 * unactionable. None of them is repaired: this is an append-only table, and a
 * console that quietly sorted or renumbered it would be the only party that
 * knew anything was amiss.
 */
interface TrailFindings {
  /** Events whose `seq` did not arrive as an exact integer. */
  readonly unnumbered: number
  /** A numbered event that did not follow the numbered event before it. */
  readonly outOfOrder: boolean
  /** Numbers skipped between consecutive numbered events. */
  readonly gaps: number
  /** The lowest number on the trail, when there is one. */
  readonly startsAt: number | null
  /** Action strings outside the seven, shown so an operator can quote them. */
  readonly unknownActions: readonly string[]
  /** The last readable `closure_proposed`, stamped — `''` if not established. */
  readonly proposalAt: string
  /** Whether any readable event proposed a closure. */
  readonly sawProposal: boolean
}

/**
 * The trail's findings, read from the events that parsed.
 *
 * `proposalAt` is deliberately withheld whenever ANY entry was unreadable, and
 * that is the fussiest rule in this file. The trail is a sequence, so an
 * unreadable entry may be a later proposal, a withdrawal, or an approval — and
 * "proposed at 09:14" over a trail with a hole in it is a fact about the wrong
 * event, stated with the confidence of one that was read. An age nobody can
 * establish is reported as not established.
 */
function findingsOf(events: readonly EventRow[], unreadable: number): TrailFindings {
  const numbered = events.map((e) => e.seq).filter((n): n is number => n !== null)

  let outOfOrder = false
  let gaps = 0
  for (let i = 1; i < numbered.length; i += 1) {
    const prev = numbered[i - 1]
    const here = numbered[i]
    if (prev === undefined || here === undefined) continue
    if (here <= prev) outOfOrder = true
    else gaps += here - prev - 1
  }

  const unknownActions = [...new Set(
    events
      .filter((e) => !Object.prototype.hasOwnProperty.call(ACTION_TONE, e.action))
      .map((e) => (e.action === '' ? '(empty)' : e.action)),
  )]

  const proposals = events.filter((e) => e.action === 'closure_proposed')
  // The LAST proposal, because a withdrawal and a re-proposal are both ordinary
  // and only the most recent one is the proposal now awaiting a checker.
  const latest = proposals.length === 0 ? undefined : proposals[proposals.length - 1]
  const proposalAt = unreadable === 0 && latest !== undefined
    ? properStamp(latest.at) ?? ''
    : ''

  return {
    unnumbered: events.length - numbered.length,
    outOfOrder,
    gaps,
    startsAt: numbered.length === 0 ? null : Math.min(...numbered),
    unknownActions,
    proposalAt,
    sawProposal: proposals.length > 0,
  }
}

/**
 * The count under the trail.
 *
 * Two facts, kept apart for the reason the queue keeps three apart: how many
 * events were read, and how many arrived and could not be. One number covering
 * both would make a trail with a hole in it read as a complete trail.
 *
 * It says "in the order the backend returned them" and never "oldest first".
 * Oldest-first is what the backend intends and what the trail almost always is,
 * but it is a property of the payload rather than of this read — asserting it
 * in the footer would be the console vouching for an ordering it has not
 * checked, on the one view where a wrong order is the finding.
 */
function trailFooter(read: number, unreadable: number): string {
  const head = `${String(read)} event(s) read, in the order the backend returned them`
  return unreadable === 0
    ? head
    : `${head} · ${String(unreadable)} further entr(y/ies) arrived and could not be read as events`
}

function trailTable(events: readonly EventRow[], unreadable: number, ref: string): TableBlock {
  return {
    t: 'table',
    title: `Case history — ${ref}`,
    // Our own record, read now. `provider` would attribute our case handling to
    // the institution, inverting the design — we handle, they decide.
    src: 'ledger',
    facets: false,
    cols: TRAIL_COLS,
    rows: events.map(eventCells),
    footer: trailFooter(events.length, unreadable),
    foot2:
      'Append-only: aml_case_events refuses UPDATE, DELETE and TRUNCATE at the database, and aml_cases.state is a projection of this table rather than the other way round. Nothing here edits, reorders or renumbers it — the rows are drawn in the order they arrived, with each seq beside them, so a trail that came back out of order looks out of order.',
  }
}

// ---------------------------------------------------------------------------
// The case, as the operator reads it
// ---------------------------------------------------------------------------

/** Whether the state is one of the four this console knows. */
function stateKnown(state: string): boolean {
  return Object.prototype.hasOwnProperty.call(STATE_TONE, state)
}

/**
 * `needs_a_human`, in three states and never in two.
 *
 * This is the field #46 exists to get right. `=== true` turns a field that did
 * not arrive into the reassuring answer, on the one view whose purpose is that
 * an escalation may not be buried (law 15); `!== false` turns it into an alarm
 * nobody raised. Neither is a reading — they are defaults dressed as one, and
 * the third state is the honest output.
 *
 * The backend derives this from the state (`open || closure_proposed`), so the
 * two can be checked against each other. When they disagree, neither is stated
 * as the answer — the same posture the queue takes to a depth its own page
 * contradicts.
 */
function needsAHumanItem(c: CaseRow): KvItem {
  if (c.needsAHumanStated === null) {
    return ['needs a human', '— not stated', 'not stated', 'r',
      'This read carried no readable needs_a_human. That is NOT a no. A case the console cannot say needs a human is a case nobody is told to pick up, and an escalation nobody picks up is the burial law 15 exists to prevent.',
      '']
  }

  const shouldNeed = c.state === 'open' || c.state === 'closure_proposed'
  if (stateKnown(c.state) && c.needsAHumanStated !== shouldNeed) {
    return ['needs a human',
      c.needsAHumanStated ? 'yes — the state disagrees' : 'no — the state disagrees',
      'disagrees', 'r',
      `The backend derives this from the state, and on this read the two do not agree: the case is ${c.state}, which means ${shouldNeed ? 'yes' : 'no'}, and needs_a_human came back ${String(c.needsAHumanStated)}. Neither is stated here as the answer.`,
      '']
  }

  return c.needsAHumanStated
    ? ['needs a human', 'yes', 'still work', 'a',
        'The case is open, or a closure is waiting for a countersignature. Either way an operator still has to act on it.', '']
    : ['needs a human', 'no', '', 'n',
        'The backend states no operator is currently waiting to act on this case. That is not a finding of ours about the customer: first-line handling is ours and the decision is the institution’s.', '']
}

/**
 * The case's own fields.
 *
 * The reference is first, mono, and printed exactly as it arrived — an operator
 * quotes it into an email to the institution, and a reference this console
 * tidied is a reference that does not match the one in the case book.
 */
function caseSummary(c: CaseRow, events: readonly EventRow[], unreadable: number): KvItem[] {
  const [stateLabel, stateTone] = stateChip(c)
  const trailCount = unreadable === 0
    ? `${String(events.length)}`
    : `${String(events.length)} read · ${String(unreadable)} unreadable`

  return [
    ['case reference', c.ref === '' ? '— not returned' : c.ref, '',
      c.ref === '' ? 'r' : 'n',
      c.ref === ''
        ? 'The payload carried no case reference. Nothing on this view can be quoted to the institution, because this console cannot say which case it is describing.'
        : 'The public reference, as the backend spelled it. Quote this, not the row id.',
      'm'],
    ['customer id', c.customerId === null ? '— not stated exactly' : String(c.customerId), '',
      c.customerId === null ? 'r' : 'n',
      c.customerId === null
        ? 'The customer id did not arrive as a number this console can state exactly. A plausible-looking id is one an operator looks up, finds, and acts on the wrong account for.'
        : 'The subject of the case. No customer name is on this read.',
      'm'],
    ['state', stateLabel, '', stateTone,
      stateKnown(c.state)
        ? 'From aml_cases.state, which is a projection of the trail below. A proposed closure is not a closed case.'
        : 'This state is not one of the four this console knows. It is shown verbatim rather than resolved to whichever of the four it most resembles.',
      ''],
    ['reason code', c.reasonCode === '' ? '— not recorded' : c.reasonCode, '',
      c.reasonCode === '' ? 'r' : 'n',
      c.reasonCode === ''
        ? 'The case carries no reason code, and the column is NOT NULL with a non-blank CHECK — so this is shape drift rather than a case that was raised without a reason.'
        : 'Shown exactly as the backend sent it. The console holds no vocabulary of AML reason codes and does not translate one: a code it renamed is a code that no longer matches the case book.',
      'm'],
    ['raised by', c.openedBy === '' ? '— unattributed' : c.openedBy, '',
      c.openedBy === '' ? 'r' : 'n',
      c.openedBy === ''
        ? 'Nobody is named as having raised this case. Every case action is attributable by constraint, and an unattributable one also means this console cannot say who is barred from approving a closure.'
        : 'Barred from approving a closure of this case (ErrOpenerCannotApprove).',
      ''],
    ['raised', eventAt(c.openedAt), '', properStamp(c.openedAt) === null ? 'r' : 'n',
      properStamp(c.openedAt) === null
        ? 'The case has no readable raised-at. Age is the whole of an escalation queue’s urgency, so this one cannot be aged.'
        : 'When the case was raised. UTC, because operators read this across timezones.',
      'm'],
    ['closure proposed by', proposerCell(c), '',
      awaitsChecker(c) && c.proposedBy === '' ? 'r' : 'n',
      awaitsChecker(c) && c.proposedBy === ''
        ? 'The case is in closure_proposed and nobody is named as the proposer. Without the name, no operator can be told whether they are the maker, and the maker-checker rule stops being enforceable at the point a human decides to click.'
        : 'The maker of a proposed closure, and barred from approving it (ErrSameOperator).',
      ''],
    needsAHumanItem(c),
    ['events on the trail', trailCount, '',
      unreadable > 0 ? 'r' : events.length === 0 ? 'r' : 'n',
      unreadable > 0
        ? 'Entries arrived that this console could not read as events. They are counted rather than dropped: the trail below is shorter than the record by exactly this many.'
        : events.length === 0
          ? 'No event arrived at all. This is the count of what was read, not a statement that nothing happened to the case.'
          : 'Every entry the backend returned was read. The trail below is the whole of what arrived.',
      ''],
    // The law statement, not a figure, and the one an auditor opens a case to
    // confirm: there is no endpoint behind this view that records a conclusion
    // of ours, and no state in the machine that means one.
    ['SAR filed by us', 'never', 'n/a', 'n',
      'First-line handling is ours; the filing decision is the institution’s. A case goes to them or is closed with no action, and this console records neither a suspicion score nor a verdict.',
      'b'],
  ]
}

// ---------------------------------------------------------------------------
// What the trail could not answer for
// ---------------------------------------------------------------------------

function emptyTrailNote(ref: string): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Case history',
    title: 'This case came back with no trail at all',
    body: 'The backend returned the case and an empty history. That is not a statement that nothing happened to it — it is the absence of the record that would say. The trail is what names who raised the case, who escalated it and who proposed closing it, and aml_cases.state is derived from that same table, so a state above with no event beneath it is two answers from one read that do not corroborate each other.',
    ref: `Nothing to replay for ${ref}. An empty trail is not a quiet case.`,
  }
}

function unreadableTrailNote(unreadable: number, read: number, ref: string): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Not shown',
    title: `${String(unreadable)} entr(y/ies) on this trail could not be read`,
    body: `The backend sent entries this console could not read as events, and ${String(read)} that it could. They are counted rather than dropped, because an event missing from an append-only trail is a step nobody can see was taken — and because whichever of them it was, the trail above is no longer the whole of what happened.`,
    ref: `An unread event is not an absent one. Case ${ref}.`,
  }
}

/**
 * Everything the numbering says, in one note or none.
 *
 * Stated rather than fixed. A gap on a `UNIQUE (case_id, seq)` column means an
 * event exists that this read did not return, or one that never arrived; an
 * out-of-order pair means the sequence the console is drawing is not the
 * sequence the record holds. Sorting them away is the one response that leaves
 * nobody knowing.
 */
function trailIntegrityNote(f: TrailFindings, unreadable: number, ref: string): NoteBlock | null {
  const found: string[] = []
  if (f.outOfOrder) {
    found.push('the sequence numbers do not increase down the trail, so the order these events are drawn in is not the order the record holds them in')
  }
  if (f.gaps > 0) {
    found.push(unreadable === 0
      ? `${String(f.gaps)} sequence number(s) are missing between the events that arrived, and every entry on this read was readable — so the skipped events were not returned at all`
      : `${String(f.gaps)} sequence number(s) are missing between the events that arrived, which may be the ${String(unreadable)} unreadable entr(y/ies) or may be events that were never returned`)
  }
  if (f.unnumbered > 0) {
    found.push(`${String(f.unnumbered)} event(s) arrived without a sequence number this console can state exactly, so their place in the trail is not established`)
  }
  // `!== 1` rather than `> 1`. A per-case seq is assigned from 1 upwards and is
  // UNIQUE per case, so a trail starting at 7 is missing its beginning and one
  // starting at 0 or below is numbered in a way this console does not recognise
  // — and `> 1` would let the second through silently, leaving the note below
  // free to claim the numbering "runs consecutively from 1" when it does not.
  if (f.startsAt !== null && f.startsAt !== 1) {
    found.push(`the lowest sequence number here is ${String(f.startsAt)}, and a case trail is numbered from 1, so this is not the whole of it`)
  }
  if (f.unknownActions.length > 0) {
    found.push(`action(s) ${f.unknownActions.join(', ')} are not among the seven this console knows and are shown verbatim rather than resolved to the nearest one`)
  }
  if (found.length === 0) return null

  return {
    t: 'note',
    tone: 'r',
    kicker: 'Case history',
    title: 'This trail does not read as a complete, ordered record',
    body: `${found.join('. ')}. None of it is repaired here: aml_case_events is append-only, and a console that re-sorted or renumbered a trail would be the only party that knew anything was amiss.`,
    ref: `Case ${ref} · the trail is the record; the state is a projection of it.`,
  }
}

/**
 * The trail read cleanly, which is worth saying out loud.
 *
 * Only over a trail with events on it and nothing unreadable, and only `n` —
 * never `g`. "Complete and in order" is a statement about the record's shape,
 * not about the case going well, and green on a compliance view teaches an
 * operator to read colour instead of words.
 */
function trailIntactNote(count: number, ref: string): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'Case history',
    title: 'The trail is complete and in order, as read',
    body: `All ${String(count)} entries the backend returned were read as events, their sequence numbers run consecutively from 1, and every action is one of the seven. Nothing is missing from what arrived — which is a statement about this read, not a guarantee about the table.`,
    ref: `Case ${ref} · append-only, replayable, and unedited by this console.`,
  }
}

/**
 * What this view could not answer for, in the operator's words.
 *
 * Drives the badge. Empty means every field and every entry was read, and only
 * then does the view claim `live` — unlike the queue, which is permanently
 * `partial` because the designed screen draws a value column and an SLA bar
 * this endpoint cannot supply. There is no designed case screen making a wider
 * claim here, so a complete read really is complete.
 */
function caseMissing(
  c: CaseRow, events: readonly EventRow[], unreadable: number, f: TrailFindings,
): string[] {
  const missing: string[] = []

  if (c.ref === '') missing.push('the case reference')
  if (c.customerId === null) missing.push('the customer id')
  if (c.reasonCode === '') missing.push('the reason code')
  if (c.openedBy === '') missing.push('who raised the case')
  if (properStamp(c.openedAt) === null) missing.push('when the case was raised')
  if (c.needsAHumanStated === null) missing.push('whether the case still needs a human')
  if (!stateKnown(c.state)) {
    missing.push(`the meaning of state ${c.state === '' ? '(empty)' : c.state}`)
  }
  if (awaitsChecker(c) && c.proposedBy === '') missing.push('who proposed the closure')

  if (events.length === 0 && unreadable === 0) missing.push('the case trail (no event arrived)')
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} unreadable trail entr(y/ies)`)
    // Named apart from the count because it is a different thing the operator
    // was not told: not "some events are missing" but "the age of the proposal
    // awaiting a countersignature was not established".
    if (awaitsChecker(c)) missing.push('how long the proposed closure has been waiting')
  }
  if (f.gaps > 0) missing.push(`${String(f.gaps)} event(s) skipped in the sequence`)
  if (f.unnumbered > 0) missing.push(`where ${String(f.unnumbered)} event(s) sit in the trail`)
  if (f.outOfOrder) missing.push('the true order of the trail')
  if (f.unknownActions.length > 0) {
    missing.push(`the meaning of action(s) ${f.unknownActions.join(', ')}`)
  }
  // The trail is the record and the state is derived from it, so a proposal the
  // state asserts and the trail does not record is a disagreement between two
  // halves of one read — and only sayable over a trail that was read in full.
  if (awaitsChecker(c) && unreadable === 0 && events.length > 0 && !f.sawProposal) {
    missing.push('the closure_proposed event the state implies, which is not on the trail')
  }

  return missing
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * Folds one case and its trail into the blocks a case view renders.
 *
 * Pure, and separate from the fetch, so every absence path is table-testable
 * without a client (CLAUDE.md Part III). `readAt` is a parameter for the reason
 * the backend's decision functions take `now`, and it is the CONSOLE's clock:
 * this endpoint sends no `generated_at`, so the only honest stamp available is
 * the moment the console read it.
 */
export function summariseAMLCase(
  detail: AMLCaseDetailView,
  readAt: Date,
  caller: Caller,
  api: ApiClient,
  outcomes: readonly string[],
): ScreenData {
  // The case's own fields go through the queue's reader, so a case and the row
  // an operator clicked to reach it cannot describe the same case differently.
  const c = readCase(detail) ?? {
    ref: '', customerId: null, state: '', reasonCode: '', openedBy: '', openedAt: '',
    proposedBy: '', needsAHumanStated: null, needsAHuman: true,
  }

  const events: EventRow[] = []
  let unreadable = 0
  for (const raw of detail.history) {
    const parsed = readEvent(raw)
    if (parsed === null) unreadable += 1
    else events.push(parsed)
  }

  const findings = findingsOf(events, unreadable)
  const shownRef = c.ref === '' ? '— no reference' : c.ref

  const blocks: Block[] = [
    { t: 'kv', title: `AML case ${shownRef}`, items: caseSummary(c, events, unreadable) },
    trailTable(events, unreadable, shownRef),
  ]

  if (unreadable > 0) blocks.push(unreadableTrailNote(unreadable, events.length, shownRef))
  // Empty and unreadable are separate notes and both can render: "nothing
  // parsed" and "nothing was sent" are different failures, and a read that hit
  // both must not report only the second.
  if (events.length === 0 && unreadable === 0) blocks.push(emptyTrailNote(shownRef))

  const integrity = trailIntegrityNote(findings, unreadable, shownRef)
  if (integrity !== null) blocks.push(integrity)
  else if (events.length > 0 && unreadable === 0) {
    blocks.push(trailIntactNote(events.length, shownRef))
  }

  // Law 15: the two slots render for a case awaiting a countersignature, and
  // the checker slot now states whether the operator READING it is one of the
  // two people barred from signing.
  const refusal = closureApprovalRefusal(caller, { openedBy: c.openedBy, proposedBy: c.proposedBy })
  if (awaitsChecker(c)) {
    blocks.push(closureApproval(c, {
      proposalAt: findings.proposalAt,
      refusal,
      wired: refusal === '',
      submitsNothing: '',
    }))
  }

  // The acts, in the order a case moves through them. Every one of them renders
  // whatever the state — an act the case refuses is drawn DISABLED with the
  // domain's own sentence rather than hidden, so an operator can see the whole
  // shape of the workflow and where this case sits in it (law 18). Hiding them
  // would make a case look like it had fewer options than it has, which on a
  // compliance queue is the difference between "not available yet" and "not
  // possible here".
  const standing: CaseStanding = {
    ref: c.ref, state: c.state, openedBy: c.openedBy, proposedBy: c.proposedBy,
  }
  blocks.push(
    noteForm(api, caller, standing),
    escalateForm(api, caller, standing),
    acknowledgeForm(api, caller, standing),
    resolveForm(api, caller, standing, outcomes),
    proposeClosureForm(api, caller, standing),
    withdrawClosureForm(api, caller, standing),
    approveClosureForm(api, caller, standing),
  )

  const missing = caseMissing(c, events, unreadable, findings)
  const readStamp = readAtStamp(readAt)
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: CASE_SOURCE, authority: AUTHORITY, readAt: readStamp }
    : { kind: 'partial', source: CASE_SOURCE, authority: AUTHORITY, readAt: readStamp, missing }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Reading it, fail-closed
// ---------------------------------------------------------------------------

/**
 * What the lookup found. Every branch resolves; none of them throws.
 *
 * Three answers rather than two, for the reason `DecisionLookup` has three: a
 * backend that says "no such case" and a backend that could not be asked are
 * different findings, and an operator does something different about each. The
 * first sends them back to check the reference; the second sends them to
 * whoever owns the deployment.
 *
 * `decisionRef` sits on `unread` alone because only a refusal carries one (law
 * 13). Putting it on every branch would invite a caller to render an empty
 * string as though a refusal had failed to supply one.
 */
export type AMLCaseRead =
  | { readonly kind: 'read'; readonly ref: string; readonly data: ScreenData }
  | { readonly kind: 'absent'; readonly ref: string; readonly data: ScreenData }
  | {
      readonly kind: 'unread'
      readonly ref: string
      readonly reason: string
      readonly decisionRef: string | null
      readonly data: ScreenData
    }

/**
 * The view when the case could not be read.
 *
 * One note and nothing else. There is no fallback to the design corpus, for the
 * reason the queue has none: an invented AML case with an invented trail on a
 * compliance view is worse than a blank one, and a fabricated empty trail is
 * worse still, because "nothing happened to this case" is the most consequential
 * sentence this view can say.
 */
function caseNotRead(ref: string, reason: string, decisionRef: string | null): ScreenData {
  const quoted = ref === '' ? '(no reference was given)' : ref
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'AML case',
      title: `Case ${quoted} could not be read`,
      body: `${reason} Nothing about this case is known from this read — not its state, not whether it needs a human, and not one line of its trail. That is different from a case with an empty trail, and different again from no such case existing.`,
      ref: decisionRef === null
        ? `Reference asked for: ${quoted}`
        : `decision_ref ${decisionRef} · reference asked for: ${quoted}`,
    }],
    provenance: { kind: 'unread', source: CASE_SOURCE, reason },
  }
}

/**
 * The view when the backend says there is no such case.
 *
 * Tone `a`, not `r`, and the reason is worth stating: a mistyped reference is
 * the ordinary cause of this answer, and a console that renders the ordinary
 * case as an incident teaches operators to dismiss the colour. The alarming
 * reading is named in the body instead, where it can be qualified — `aml_cases`
 * refuses DELETE and refuses reopening, so a reference that came off a queue
 * row and now resolves to nothing has not been tidied away, and that IS worth
 * raising.
 */
function caseAbsent(ref: string): ScreenData {
  const reason = `The backend answered that no case has reference ${ref}.`
  return {
    blocks: [{
      t: 'note',
      tone: 'a',
      kicker: 'AML case',
      title: `No case has reference ${ref}`,
      body: 'The endpoint answered, and its answer is that no case carries this reference. A mistyped or stale reference is the ordinary cause. The other cause is not ordinary: aml_cases refuses deletion and refuses reopening, so a reference taken from a queue row or quoted in another case’s trail cannot have been removed — if that is where this one came from, it is missing rather than absent, and it is worth raising.',
      ref: `Reference asked for: ${ref}`,
    }],
    provenance: { kind: 'unread', source: CASE_SOURCE, reason },
  }
}

/**
 * The handler's own "no such case", as opposed to any other 404.
 *
 * The AML routes are registered only when the backend was wired with a case
 * source (`if a.amlCases != nil` in `admin_routes.go`), so on a deployment
 * without one the request falls through to the mux's bare-text 404. Same status
 * code, opposite meanings — "this case does not exist" versus "this deployment
 * cannot answer that question" — and reporting the second as the first would
 * tell an operator a case had vanished from an append-only table.
 */
function isHandlerNotFound(error: ApiError): boolean {
  return error.status === 404 && error.code === 'not_found'
}

/** Why a status other than a handled 404 tells us nothing about the case. */
function whyCaseUnread(error: ApiError): string {
  const code = error.code === null ? '' : ` (${error.code})`
  const answered = `The case endpoint answered ${String(error.status)}${code}.`

  if (error.status === 401) return `${answered} The session is not authenticated.`
  if (error.status === 403) {
    // Quote the refusal; do not restate the permission model. The backend sends
    // `required_role` on an RBAC denial precisely so the console does not carry
    // a second copy of a model it cannot keep in step — and the copy that was
    // here was already wrong: `PermAMLCaseRead` is held by Support by design,
    // so "Compliance-only, and a support session sees neither" told a refused
    // operator the opposite of the truth AND discarded the role the client had
    // already parsed. A refusal that named no role gets no invented one.
    const named = text(error.requiredRole)
    return named === ''
      ? `${answered} It named no required role, so what this session is missing is not stated here rather than guessed at.`
      : `${answered} It states the required role as ${JSON.stringify(named)}.`
  }
  if (error.status === 404) {
    return `${answered} It did not carry the handler’s not-found code, so this is not "no such case" — the AML routes are registered only where the backend was wired with a case source, and this deployment may not have been.`
  }
  return answered
}

function isAMLCaseDetail(payload: unknown): payload is AMLCaseDetailView {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return false
  return Array.isArray((payload as Partial<AMLCaseDetailView>).history)
}

/**
 * Reads one AML case and its trail.
 *
 * Resolves for every answer the backend gives, per the loader contract in
 * `provenance.ts`: an endpoint that refused, failed or does not exist is
 * something this view reports, not an exception that collapses it. An abort is
 * the one thing that propagates — a navigation away is a cancellation, not a
 * finding about the case.
 *
 * Reads only. The seven POST routes on this case are deliberately not called.
 */
export async function readAMLCase(api: ApiClient, ref: string): Promise<AMLCaseRead> {
  const wanted = ref.trim()
  if (wanted === '') {
    const reason = 'No case reference was given, so nothing was looked up.'
    return { kind: 'unread', ref, reason, decisionRef: null, data: caseNotRead('', reason, null) }
  }

  // Started only once there is something to look up. Hoisting it above the
  // guard would make an empty reference call `/me` — a request on behalf of a
  // read that is not happening, and one this function promises not to make.
  const calling = callerBeside(api)

  let payload: unknown
  try {
    payload = await api.get<unknown>(amlCasePath(wanted))
  } catch (error) {
    if (error instanceof ApiError) {
      if (isHandlerNotFound(error)) {
        return { kind: 'absent', ref: wanted, data: caseAbsent(wanted) }
      }
      const reason = whyCaseUnread(error)
      return {
        kind: 'unread',
        ref: wanted,
        reason,
        // Law 13: the reference travels as itself, not folded into prose. It is
        // what an operator copies to find out why they were refused.
        decisionRef: error.decisionRef,
        data: caseNotRead(wanted, reason, error.decisionRef),
      }
    }
    // A navigation away is a cancellation, not a report about the case.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const reason = 'The backend could not be reached.'
    return {
      kind: 'unread', ref: wanted, reason, decisionRef: null,
      data: caseNotRead(wanted, reason, null),
    }
  }

  if (!isAMLCaseDetail(payload)) {
    const reason = 'The case endpoint answered with a shape this console does not know: no history array was on it.'
    return {
      kind: 'unread', ref: wanted, reason, decisionRef: null,
      data: caseNotRead(wanted, reason, null),
    }
  }

  // The case has to be the one we asked about. Rendering another case's state
  // and trail under this reference would be the most convincing wrong answer
  // this view could give — every word of it read from the backend, and all of
  // it about a different escalation.
  // Compared case-INSENSITIVELY, because `case_ref` is CITEXT in Postgres. The
  // backend resolves "AML-2211" and "aml-2211" to the same row, so an exact
  // comparison here throws away a case that was read correctly and reports it
  // as unreadable — the identity check firing on the identity holding.
  const answered = text(payload.case_ref)
  if (answered.toLowerCase() !== wanted.toLowerCase()) {
    const reason = answered === ''
      ? 'The case endpoint answered without a case reference, so this console cannot confirm the case it described is the one that was asked for.'
      : `The case endpoint answered about ${JSON.stringify(answered)}, which is not the reference that was looked up.`
    return {
      kind: 'unread', ref: wanted, reason, decisionRef: null,
      data: caseNotRead(wanted, reason, null),
    }
  }

  const caller = await calling
  return {
    kind: 'read',
    ref: wanted,
    data: summariseAMLCase(
      payload, new Date(), caller, api,
      await recordableOutcomes(api, wanted, text(payload.state)),
    ),
  }
}

/**
 * The outcomes the server will accept for this case's escalation.
 *
 * # Why they are read rather than written down here
 *
 * `GET /aml/cases/{ref}/escalation` carries `recordable_outcomes` explicitly so
 * the console "renders the two recordable outcomes rather than a free-text box
 * somebody types a filing into". A copy of the pair in this file would be the
 * second implementation that field exists to prevent, and it would go stale
 * silently — the set is small enough that nobody would notice it had.
 *
 * So an empty answer here disables the control rather than falling back to a
 * literal. That is the honest end of the same rule: what this console cannot
 * read, it does not invent.
 *
 * # Only for a case that is with the institution
 *
 * A case that was never escalated has no escalation record, and asking for one
 * answers 404 — a refusal in the log and in the network panel that says nothing
 * except that the console asked a question it already knew the answer to.
 *
 * # Every failure is an empty list, including the interesting one
 *
 * A missing permission (`aml.escalation.package.read` is Compliance-only and
 * narrower than the case read), a schema version this release cannot render, an
 * unreachable backend: all of them mean the same thing HERE, which is that the
 * option list is not known. The `resolve` control says so in its own words. An
 * `AbortError` still propagates, because a cancelled navigation is not a
 * finding about the outcomes.
 */
async function recordableOutcomes(
  api: ApiClient, ref: string, state: string,
): Promise<readonly string[]> {
  if (state !== 'escalated') return []
  let payload: unknown
  try {
    payload = await api.get<unknown>(`${amlCasePath(ref)}/escalation`)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return []
  }
  if (typeof payload !== 'object' || payload === null) return []
  const raw = (payload as Record<string, unknown>)['recordable_outcomes']
  if (!Array.isArray(raw)) return []
  // Narrowed one by one rather than cast: a malformed entry drops out instead
  // of reaching a `<select>` as `undefined` and submitting an empty outcome,
  // which the handler would refuse with a 400 the operator cannot act on.
  return raw.filter((o): o is string => typeof o === 'string' && o !== '')
}
